import React, { useState } from 'react';
import { 
  TrendingUp, 
  DollarSign, 
  Bed,
  Star,
  AlertCircle,
  Brain,
  Zap,
  Utensils,
  Calendar
} from 'lucide-react';

interface FinancialMetric {
  label: string;
  value: number;
  change: number;
  changeType: 'increase' | 'decrease';
  icon: React.ReactNode;
  color: string;
}

interface RevenueData {
  month: string;
  roomRevenue: number;
  foodRevenue: number;
  otherRevenue: number;
  totalRevenue: number;
}

const Insights: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  // Different data sets for different periods
  const getFinancialData = (period: string) => {
    const dataSets = {
      week: {
        metrics: [
          { label: 'Occupancy Rate', value: 82.5, change: 3.2, changeType: 'increase' as const, icon: <Bed className="w-6 h-6" />, color: 'text-blue-600' },
          { label: 'Guest Satisfaction', value: 4.8, change: 0.3, changeType: 'increase' as const, icon: <Star className="w-6 h-6" />, color: 'text-green-600' },
          { label: 'Staff Efficiency', value: 92, change: 5.1, changeType: 'increase' as const, icon: <TrendingUp className="w-6 h-6" />, color: 'text-orange-600' },
          { label: 'Restaurant Utilization', value: 78, change: -2.4, changeType: 'decrease' as const, icon: <DollarSign className="w-6 h-6" />, color: 'text-purple-600' }
        ],
        revenueData: [
          { day: 'Mon', roomRevenue: 45000, foodRevenue: 18000, otherRevenue: 5000, totalRevenue: 68000 },
          { day: 'Tue', roomRevenue: 52000, foodRevenue: 22000, otherRevenue: 6000, totalRevenue: 80000 },
          { day: 'Wed', roomRevenue: 48000, foodRevenue: 19000, otherRevenue: 5500, totalRevenue: 72500 },
          { day: 'Thu', roomRevenue: 55000, foodRevenue: 25000, otherRevenue: 7000, totalRevenue: 87000 },
          { day: 'Fri', roomRevenue: 62000, foodRevenue: 28000, otherRevenue: 8000, totalRevenue: 98000 },
          { day: 'Sat', roomRevenue: 68000, foodRevenue: 32000, otherRevenue: 9000, totalRevenue: 109000 },
          { day: 'Sun', roomRevenue: 45000, foodRevenue: 18000, otherRevenue: 5000, totalRevenue: 68000 }
        ]
      },
      month: {
        metrics: [
          { label: 'Occupancy Rate', value: 78.5, change: -2.1, changeType: 'decrease' as const, icon: <Bed className="w-6 h-6" />, color: 'text-blue-600' },
          { label: 'Guest Satisfaction', value: 4.7, change: 0.2, changeType: 'increase' as const, icon: <Star className="w-6 h-6" />, color: 'text-green-600' },
          { label: 'Staff Efficiency', value: 89, change: 3.2, changeType: 'increase' as const, icon: <TrendingUp className="w-6 h-6" />, color: 'text-orange-600' },
          { label: 'Restaurant Utilization', value: 75, change: 4.1, changeType: 'increase' as const, icon: <DollarSign className="w-6 h-6" />, color: 'text-purple-600' }
        ],
        revenueData: [
          { week: 'W1', roomRevenue: 650000, foodRevenue: 240000, otherRevenue: 80000, totalRevenue: 970000 },
          { week: 'W2', roomRevenue: 720000, foodRevenue: 280000, otherRevenue: 90000, totalRevenue: 1090000 },
          { week: 'W3', roomRevenue: 680000, foodRevenue: 260000, otherRevenue: 85000, totalRevenue: 1025000 },
          { week: 'W4', roomRevenue: 750000, foodRevenue: 300000, otherRevenue: 95000, totalRevenue: 1145000 }
        ]
      },
      quarter: {
        metrics: [
          { label: 'Occupancy Rate', value: 76.2, change: 4.3, changeType: 'increase' as const, icon: <Bed className="w-6 h-6" />, color: 'text-blue-600' },
          { label: 'Guest Satisfaction', value: 4.6, change: 0.4, changeType: 'increase' as const, icon: <Star className="w-6 h-6" />, color: 'text-green-600' },
          { label: 'Staff Efficiency', value: 87, change: 6.8, changeType: 'increase' as const, icon: <TrendingUp className="w-6 h-6" />, color: 'text-orange-600' },
          { label: 'Restaurant Utilization', value: 72, change: 8.2, changeType: 'increase' as const, icon: <DollarSign className="w-6 h-6" />, color: 'text-purple-600' }
        ],
        revenueData: [
          { month: 'Month 1', roomRevenue: 2800000, foodRevenue: 1100000, otherRevenue: 350000, totalRevenue: 4250000 },
          { month: 'Month 2', roomRevenue: 2950000, foodRevenue: 1200000, otherRevenue: 380000, totalRevenue: 4530000 },
          { month: 'Month 3', roomRevenue: 3100000, foodRevenue: 1300000, otherRevenue: 400000, totalRevenue: 4800000 }
        ]
      },
      year: {
        metrics: [
          { label: 'Occupancy Rate', value: 74.8, change: 6.2, changeType: 'increase' as const, icon: <Bed className="w-6 h-6" />, color: 'text-blue-600' },
          { label: 'Guest Satisfaction', value: 4.5, change: 0.6, changeType: 'increase' as const, icon: <Star className="w-6 h-6" />, color: 'text-green-600' },
          { label: 'Staff Efficiency', value: 85, change: 12.4, changeType: 'increase' as const, icon: <TrendingUp className="w-6 h-6" />, color: 'text-orange-600' },
          { label: 'Restaurant Utilization', value: 68, change: 15.3, changeType: 'increase' as const, icon: <DollarSign className="w-6 h-6" />, color: 'text-purple-600' }
        ],
        revenueData: [
          { quarter: 'Q1', roomRevenue: 8500000, foodRevenue: 3200000, otherRevenue: 1000000, totalRevenue: 12700000 },
          { quarter: 'Q2', roomRevenue: 9200000, foodRevenue: 3600000, otherRevenue: 1100000, totalRevenue: 13900000 },
          { quarter: 'Q3', roomRevenue: 9800000, foodRevenue: 3800000, otherRevenue: 1200000, totalRevenue: 14800000 },
          { quarter: 'Q4', roomRevenue: 10500000, foodRevenue: 4000000, otherRevenue: 1300000, totalRevenue: 15800000 }
        ]
      }
    };
    return dataSets[period as keyof typeof dataSets] || dataSets.month;
  };

  const currentData = getFinancialData(selectedPeriod);


  return (
    <div className="space-y-6">
      {/* AI Insights & KPIs Combined - Redesigned */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">AI Command Center</h3>
                <p className="text-primary-100 text-sm">Intelligent insights & actionable recommendations</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="px-3 py-1 bg-green-500/20 rounded-full">
                <span className="text-green-100 text-xs font-medium">LIVE</span>
              </div>
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Critical Actions */}
            <div>
              <div className="flex items-center space-x-2 mb-6">
                <div className="p-2 bg-red-50 rounded-lg shadow-sm">
                  <AlertCircle className="w-6 h-6 text-red-500" />
                </div>
                <h4 className="text-xl font-semibold text-gray-900">Critical Actions</h4>
                <div className="px-3 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-full shadow-sm">3 URGENT</div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-5 rounded-r-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 border border-red-100">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-lg font-semibold text-red-800">Staff Shortage Alert</span>
                        <span className="px-3 py-1 bg-red-200 text-red-800 text-sm font-medium rounded-full shadow-sm">HIGH PRIORITY</span>
                      </div>
                      <p className="text-base text-red-700 mb-3">Kitchen staff shortage detected - immediate action required to maintain service quality</p>
                      <div className="flex items-center space-x-6 text-sm text-red-600">
                        <span className="font-medium">• Hire 2 kitchen staff</span>
                        <span className="font-medium">• Deadline: 2 days</span>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-3xl font-bold text-red-600">92%</div>
                      <div className="text-sm text-red-500">Staff Efficiency</div>
                    </div>
                  </div>
                </div>

                <div className="bg-primary-50 border-l-4 border-primary-500 p-5 rounded-r-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 border border-primary-100">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-lg font-semibold text-primary-800">Revenue Optimization</span>
                        <span className="px-3 py-1 bg-primary-200 text-primary-800 text-sm font-medium rounded-full shadow-sm">MEDIUM</span>
                      </div>
                      <p className="text-base text-primary-700 mb-3">Premium room pricing strategy needs adjustment to maximize revenue potential</p>
                      <div className="flex items-center space-x-6 text-sm text-primary-600">
                        <span className="font-medium">• Increase rates by 15%</span>
                        <span className="font-medium">• Deadline: 1 week</span>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-3xl font-bold text-primary-600">+25%</div>
                      <div className="text-sm text-primary-500">Revenue Growth</div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 border-l-4 border-gray-400 p-5 rounded-r-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 border border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-lg font-semibold text-gray-800">Process Improvement</span>
                        <span className="px-3 py-1 bg-gray-200 text-gray-800 text-sm font-medium rounded-full shadow-sm">LOW</span>
                      </div>
                      <p className="text-base text-gray-700 mb-3">Check-in process can be optimized for better efficiency and guest experience</p>
                      <div className="flex items-center space-x-6 text-sm text-gray-600">
                        <span className="font-medium">• Reduce time by 15 mins</span>
                        <span className="font-medium">• Deadline: 2 weeks</span>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-3xl font-bold text-gray-600">78%</div>
                      <div className="text-sm text-gray-500">Efficiency</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - AI Insights & Predictions */}
            <div>
              <div className="flex items-center space-x-2 mb-6">
                <div className="p-2 bg-primary-50 rounded-lg shadow-sm">
                  <Zap className="w-6 h-6 text-primary-500" />
                </div>
                <h4 className="text-xl font-semibold text-gray-900">AI Predictions</h4>
                <div className="px-3 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-full shadow-sm">ML POWERED</div>
              </div>
              
              <div className="space-y-5">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-green-500 rounded-xl shadow-md">
                      <TrendingUp className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="text-lg font-semibold text-green-800 mb-2">Revenue Forecast</h5>
                      <p className="text-base text-green-700 mb-3">Next month projected to see 18% increase in room revenue based on current booking patterns</p>
                      <div className="text-sm text-green-600">
                        <span className="font-medium">Confidence: 87%</span> • Based on booking trends and historical data
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl p-5 border border-primary-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-primary-500 rounded-xl shadow-md">
                      <Star className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="text-lg font-semibold text-primary-800 mb-2">Guest Satisfaction</h5>
                      <p className="text-base text-primary-700 mb-3">Predicted to reach 4.9/5 by month-end with current service improvements</p>
                      <div className="text-sm text-primary-600">
                        <span className="font-medium">Trend: +0.1</span> • Based on recent feedback analysis
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-5 border border-gray-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-gray-500 rounded-xl shadow-md">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="text-lg font-semibold text-gray-800 mb-2">Peak Season Alert</h5>
                      <p className="text-base text-gray-700 mb-3">Holiday season approaching - prepare for 40% occupancy increase and staffing needs</p>
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Starts: Dec 15</span> • 2 weeks to prepare infrastructure
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl p-5 border border-primary-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-primary-600 rounded-xl shadow-md">
                      <Utensils className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="text-lg font-semibold text-primary-800 mb-2">Menu Optimization</h5>
                      <p className="text-base text-primary-700 mb-3">Biryani dishes showing 35% higher profitability - consider expanding menu options</p>
                      <div className="text-sm text-primary-600">
                        <span className="font-medium">Action: Add 2 variants</span> • Expected +12% F&B revenue increase
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Insights;
